import { EMAIL_PATTERN } from '@sequencehq/utils/validation'
import {
  buildVariableLookup,
  VARIABLE_TOKEN_PATTERN
} from '@sequencehq/core-components'

import { isRequired } from 'Automations/nodeDefinitions/validation/validators'
import { allHandlesConnected } from 'Automations/nodeDefinitions/validation/nodeValidators'
import type { ValidatorReturn } from 'Automations/nodeDefinitions/validation'
import {
  REMINDER_NODE_RECIPIENT_TEMPLATE_VARIABLES,
  REMINDER_NODE_TEMPLATE_VARIABLES
} from './reminder.constants'

export const handlesConnected = allHandlesConnected({
  handleTypes: ['target', 'source'],
  title: 'Disconnected node',
  message: 'All handles must be connected'
})

export const toRequired = isRequired({
  title: 'Missing recipient',
  message: 'At least one recipient is required'
})

export const subjectRequired = isRequired({
  title: 'Missing subject',
  message: 'Subject is required'
})

// A cleared rich-text editor still emits markup (e.g. `<p></p>`, or `<p>&nbsp;</p>`
// after typing then deleting), so a plain truthiness check treats it as filled.
// Strip tags and whitespace entities to decide whether the body is genuinely blank.
const isBlankHtml = (value: unknown): boolean =>
  typeof value !== 'string' ||
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .trim().length === 0

export const templateRequired = (
  value: unknown
): ValidatorReturn | undefined =>
  isBlankHtml(value)
    ? { title: 'Missing template', message: 'A message template is required' }
    : undefined

export const sendReminderRequired = isRequired({
  title: 'Missing reminder schedule',
  message: 'A reminder duration must be set'
})

// Resolves a `{{token}}`'s inner text the same way the rich-text editor does
// (by lowercased key or label), so validation accepts exactly the variables the
// variable menu offers and can't drift from it.
const TEMPLATE_VARIABLE_LOOKUP = buildVariableLookup(
  REMINDER_NODE_TEMPLATE_VARIABLES
)

// The subject and body may reference any template variable. An unknown token
// (a typo, or hand-typed text like `{{foobar}}`) crashes the backend's
// generate_invoice_reminder task, so collect every unrecognised token's inner
// text — de-duplicated — to reject before the node can be saved.
const findUnknownTemplateVariables = (template: string): string[] => {
  const unknownTokens = [...template.matchAll(VARIABLE_TOKEN_PATTERN)]
    .map(match => match[1].trim())
    .filter(token => !TEMPLATE_VARIABLE_LOOKUP.has(token.toLowerCase()))

  return [...new Set(unknownTokens)]
}

// Validates a template-bearing field (subject or body). Non-string values pass —
// emptiness is enforced separately by `subjectRequired`/`templateRequired`.
export const noUnknownTemplateVariables = (
  value: unknown
): ValidatorReturn | undefined => {
  if (typeof value !== 'string') return undefined

  const unknownVariables = findUnknownTemplateVariables(value)

  if (unknownVariables.length === 0) return undefined

  const tokens = unknownVariables.map(token => `{{${token}}}`).join(', ')

  return {
    title: 'Unknown template variable',
    message:
      unknownVariables.length === 1
        ? `${tokens} is not a recognised variable. Use the variable menu to insert valid variables.`
        : `${tokens} are not recognised variables. Use the variable menu to insert valid variables.`
  }
}

// The recipient fields (To/CC/BCC) only offer the contact-email variable, so the
// only valid non-email recipients are its variable token (`{{contactEmail}}`).
// Derived from the same source the form offers so the two can't drift apart.
const RECIPIENT_VARIABLE_TOKENS = Object.keys(
  REMINDER_NODE_RECIPIENT_TEMPLATE_VARIABLES
).map(key => `{{${key}}}`)

const isValidRecipient = (recipient: unknown): boolean => {
  if (typeof recipient !== 'string') return false

  const trimmed = recipient.trim()

  return (
    RECIPIENT_VARIABLE_TOKENS.includes(trimmed) || EMAIL_PATTERN.test(trimmed)
  )
}

// Validates every entry in a recipient list (To/CC/BCC). An empty list passes —
// the "at least one recipient" rule is enforced separately by `toRequired`, and
// CC/BCC may legitimately be empty.
export const validEmailRecipients = (
  value: unknown
): ValidatorReturn | undefined => {
  if (!Array.isArray(value)) return undefined

  const hasInvalidRecipient = value.some(
    recipient => !isValidRecipient(recipient)
  )

  return hasInvalidRecipient
    ? {
        title: 'Invalid recipient',
        message: 'Enter valid email addresses or recipient variables'
      }
    : undefined
}

// Approvers are only required when the reminder is configured to require
// approval. The full node config is passed as `context`, so we can read the
// sibling `isApprovalRequired` flag.
export const approversRequiredIfApprovalRequired = (
  value: unknown,
  context?: Record<string, unknown>
): ValidatorReturn | undefined => {
  const isApprovalRequired = Boolean(context?.isApprovalRequired)

  if (!isApprovalRequired) return undefined

  const approverUserIds = Array.isArray(value) ? value : []

  return approverUserIds.length === 0
    ? {
        title: 'Missing approver',
        message: 'At least one approver is required'
      }
    : undefined
}
